import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useReducer,
  useEffect,
  useMemo,
  PropsWithChildren,
} from 'react'
import { FTSWidgetActions, FTSWidgetActionTypes } from './actions'
import {
  FTSWidgetReducer,
  IFTSWidgetState,
  initialFTSWidgetState,
} from './reducers'
import { Loading } from '../components/Loading'
import { InitializeResponse, wizardInitialize } from '../api/wizard'
import { ISystem } from '../types/tink.interface'
import { useStorage } from '../hooks/useStorage'
import { useTrack } from './track'
import { TrackingEvent } from '../utils/tracking'

interface IInitialFTSWidgetContextState {
  state: IFTSWidgetState
  dispatch: React.Dispatch<FTSWidgetActions>
  refetchKey: (iban?: string) => void
  wizardKey?: string
  error?: Error
}

interface IFTSWidgetProviderProps {
  children: ReactNode
  system: ISystem
}

export const FTS_WIZARD_KEY_STORAGE_KEY = 'fts_widget_wizard_key'
export const FTS_TRANSACTION_ID_STORAGE_KEY = 'fts_widget_transaction_id'

const throwMissingProvider = () => {
  throw new Error('FTS Widget Provider is missing')
}

export const FTSWidgetContext = createContext<IInitialFTSWidgetContextState>({
  state: initialFTSWidgetState,
  dispatch: throwMissingProvider,
  refetchKey: () => undefined,
})

const FTSWidgetProvider: FC<IFTSWidgetProviderProps & PropsWithChildren> = ({
  children,
  system,
}) => {
  const {
    clientUuid,
    personId,
    origin,
    traversalUuid,
    loanLeadUuid,
    environment,
    applicationUuid,
    customerUuid,
    firstName,
    lastName,
  } = system
  const [state, dispatch] = useReducer(FTSWidgetReducer, initialFTSWidgetState)
  const [, setWizardKey, clearFtsWizardKeyStorage] = useStorage({
    type: 'sessionStorage',
    key: FTS_WIZARD_KEY_STORAGE_KEY,
    initialValue: '',
  })
  const [, setTransactionId, clearFtsTransactionIdStorage] = useStorage({
    type: 'sessionStorage',
    key: FTS_TRANSACTION_ID_STORAGE_KEY,
    initialValue: '',
  })

  const track = useTrack()
  useEffect(() => {
    dispatch({
      type: FTSWidgetActionTypes.SET_FTS_WIDGET_TRANSACTION_DATA,
      payload: {
        clientUuid,
        personId,
        origin,
        traversalUuid,
        applicationUuid,
        customerUuid,
        firstName,
        lastName,
        loanLeadUuid,
      },
    })
  }, [
    applicationUuid,
    clientUuid,
    customerUuid,
    firstName,
    lastName,
    loanLeadUuid,
    origin,
    personId,
    traversalUuid,
  ])

  // Clear sessionStorage on unmount
  useEffect(
    () => () => {
      clearFtsWizardKeyStorage()
      clearFtsTransactionIdStorage()
    },
    [clearFtsTransactionIdStorage, clearFtsWizardKeyStorage],
  )

  const fetchKey = useCallback(
    async (iban?: string) => {
      try {
        let data: InitializeResponse | undefined
        if (iban) {
          try {
            track(TrackingEvent.FetchKey, { iban: iban.substring(0, 6) })
            data = await wizardInitialize(environment, {
              ...state.transactionLocalData,
              checkPeriodDays: system.checkPeriodDays,
              iban,
            })
          } catch (e) {
            track(TrackingEvent.FetchKeyError, {
              iban: iban.substring(0, 6),
              error: e,
            })
            console.warn(e)
            console.warn('Could not fetch key with iban. Trying without iban')
          }
        }
        if (!data) {
          track(TrackingEvent.FetchKey, { iban: 'not-set' })
          data = await wizardInitialize(environment, {
            ...state.transactionLocalData,
            checkPeriodDays: system.checkPeriodDays,
          })
        }

        dispatch({
          type: FTSWidgetActionTypes.FTS_WIDGET_FIELD_CHANGE,
          payload: {
            wizardKey: data.result.wizard_session_key,
            transactionId: data.result.transaction_id,
            requestError: undefined,
          },
        })
        setWizardKey(data.result.wizard_session_key)
        setTransactionId(data.result.transaction_id)
      } catch (error) {
        track(TrackingEvent.FetchKeyError, { iban: 'not-set', error })
        dispatch({
          type: FTSWidgetActionTypes.FTS_WIDGET_FIELD_CHANGE,
          payload: {
            requestError: error as Error,
          },
        })
      } finally {
        dispatch({
          type: FTSWidgetActionTypes.FTS_WIDGET_FIELD_CHANGE,
          payload: {
            isFetching: false,
          },
        })
      }
    },
    [
      environment,
      setTransactionId,
      setWizardKey,
      state.transactionLocalData,
      system.checkPeriodDays,
      track,
    ],
  )

  const refetchKey = useCallback(
    (iban?: string) => {
      dispatch({
        type: FTSWidgetActionTypes.FTS_WIDGET_FIELD_CHANGE,
        payload: {
          wizardKey: '',
          transactionId: '',
        },
      })
      fetchKey(iban)
    },
    [fetchKey],
  )

  const value = useMemo(
    () => ({
      state,
      dispatch,
      refetchKey,
      error: state.requestError ?? undefined,
    }),
    [refetchKey, state],
  )

  if (state.isFetching) {
    return <Loading />
  }
  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <FTSWidgetContext.Provider value={value}>
      {children}
    </FTSWidgetContext.Provider>
  )
}

export default FTSWidgetProvider

export const useFTSWidgetContext = () => useContext(FTSWidgetContext)
